import React, { useRef, useState, useEffect, useCallback } from 'react'
import {
  FeaturesSec,
  FeaturesRow,
  Card,
  CardImg,
  CardTitle,
  CardText,
  Dots,
  Heading
} from './FeaturesSection.elements'
import { Container } from '../../globalStyles'

const GAP = 20
const AUTOPLAY_MS = 4000
const RESUME_AFTER_MS = 8000

const FeaturesSection = ({ features, titulo }) => {
  const sliderRef = useRef()
  const [index, setIndex] = useState(0)
  const [count, setCount] = useState(features.length)

  const drag = useRef({ active: false, startX: 0, scrollLeft: 0 })
  const pausedUntil = useRef(0)

  // largura real de um card + espaçamento (muda conforme o breakpoint)
  const step = () => {
    const el = sliderRef.current
    const card = el && el.children[0]
    return card ? card.offsetWidth + GAP : 1
  }

  // quantidade de posições possíveis (no desktop cabem 3 cards por vez)
  const measure = useCallback(() => {
    const el = sliderRef.current
    if (!el) return
    setCount(Math.round((el.scrollWidth - el.clientWidth) / step()) + 1)
  }, [])

  useEffect(() => {
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  const goTo = useCallback(i => {
    const el = sliderRef.current
    if (!el) return
    const newIndex = i >= count ? 0 : i < 0 ? count - 1 : i
    el.scrollTo({ left: step() * newIndex, behavior: 'smooth' })
    setIndex(newIndex)
  }, [count])

  /* ================= AUTOPLAY ================= */

  useEffect(() => {
    if (count <= 1) return
    const interval = setInterval(() => {
      if (Date.now() < pausedUntil.current) return
      goTo(index + 1)
    }, AUTOPLAY_MS)
    return () => clearInterval(interval)
  }, [index, count, goTo])

  const pause = () => {
    pausedUntil.current = Date.now() + RESUME_AFTER_MS
  }

  /* ================= DRAG (mouse) ================= */
  // no toque o scroll nativo já resolve; o arraste só é necessário no mouse

  const startDrag = e => {
    pause()
    if (e.pointerType !== 'mouse') return
    drag.current = {
      active: true,
      startX: e.pageX,
      scrollLeft: sliderRef.current.scrollLeft
    }
    sliderRef.current.classList.add('dragging')
  }

  const moveDrag = e => {
    if (!drag.current.active) return
    e.preventDefault()
    sliderRef.current.scrollLeft =
      drag.current.scrollLeft - (e.pageX - drag.current.startX)
  }

  const endDrag = () => {
    if (!drag.current.active) return
    drag.current.active = false
    const el = sliderRef.current
    el.classList.remove('dragging')
    goTo(Math.round(el.scrollLeft / step()))
  }

  /* ================= DOT SYNC ================= */

  const handleScroll = () => {
    const el = sliderRef.current
    const atEnd = el.scrollLeft >= el.scrollWidth - el.clientWidth - 2
    setIndex(atEnd ? count - 1 : Math.round(el.scrollLeft / step()))
  }

  return (
    <FeaturesSec>
      <Container>
        <Heading>{titulo}</Heading>
        <FeaturesRow
          ref={sliderRef}
          onPointerDown={startDrag}
          onPointerMove={moveDrag}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
          onTouchStart={pause}
          onScroll={handleScroll}
        >
          {features.map((item, i) => (
            <Card key={i}>
              <CardImg src={item.img} alt="" draggable="false" />
              <CardTitle>{item.title}</CardTitle>
              {item.text && <CardText>{item.text}</CardText>}
            </Card>
          ))}
        </FeaturesRow>

        {count > 1 && (
          <Dots>
            {Array.from({ length: count }).map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Ir para o item ${i + 1}`}
                className={i === index ? 'active' : ''}
                onClick={() => {
                  pause()
                  goTo(i)
                }}
              />
            ))}
          </Dots>
        )}
      </Container>
    </FeaturesSec>
  )
}

export default FeaturesSection
